import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router';
import { routeConfig } from './routes';
import { ErrorBoundary } from './shell/ErrorBoundary';
import { ToastProvider } from './shell/Toast';
import { useAuthStore, type Session } from './stores/authStore';
import { initClarity, trackUncaughtError, trackUnhandledRejection } from './observability/clarity';
import { initSentry } from './observability/sentry';
import '@myskool/ui/styles.css';
import './global.css';

// Clarity: uncaught error / unhandled rejection listeners.
window.addEventListener('error', (event) => {
  if (event.error) trackUncaughtError(event.error?.constructor?.name ?? 'Error');
});
window.addEventListener('unhandledrejection', () => {
  trackUnhandledRejection();
});

// PWA freshness: the service worker uses autoUpdate + skipWaiting + clientsClaim, so a new
// deploy's worker activates and claims this page immediately — but an already-rendered page
// keeps showing the stale cached app until it reloads. Reload once when a new worker takes
// control so the latest app + routes appear without a manual hard-refresh. Guarded so it never
// reloads on the first-ever install (no prior controller) and never loops.
if ('serviceWorker' in navigator) {
  const hadController = Boolean(navigator.serviceWorker.controller);
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloading || !hadController) return;
    reloading = true;
    window.location.reload();
  });
}

// E2E test seam
if (import.meta.env.VITE_E2E === '1') {
  const w = window as unknown as {
    __E2E_SEED__?: Session;
    __mySkoolTest__?: Record<string, unknown>;
  };
  if (w.__E2E_SEED__) useAuthStore.getState().setSession(w.__E2E_SEED__);
  w.__mySkoolTest__ = {
    setSession: (s: Session) => useAuthStore.getState().setSession(s),
    expire: () => useAuthStore.getState().expire(),
    sessionExpired: () => useAuthStore.getState().sessionExpired(),
    getState: () => useAuthStore.getState(),
  };
}

// Clarity: initialized synchronously before first render.
initClarity({
  projectId: import.meta.env.VITE_CLARITY_PROJECT_ID ?? '',
  appName: import.meta.env.VITE_APP_NAME ?? 'myskool-school',
  appEnv: (import.meta.env.VITE_APP_ENV as 'development' | 'staging' | 'production') ?? (import.meta.env.PROD ? 'production' : 'development'),
  appVersion: import.meta.env.VITE_APP_VERSION,
  releaseSha: import.meta.env.VITE_RELEASE_SHA,
});

// Sentry: initialized synchronously before first render.
// No-op unless VITE_SENTRY_DSN is set and environment is enabled.
initSentry();

const router = createBrowserRouter(routeConfig);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </ErrorBoundary>
  </StrictMode>,
);
